import { motion, AnimatePresence } from "framer-motion";
import { 
  Activity, 
  LayoutDashboard, 
  Smartphone, 
  Zap, 
  BrainCircuit, 
  ScanEye,
  Users,
  TrendingUp,
  UserCheck,
  Clock
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/futuristic_medical_data_flow_background.png";
const appScreen = "/assets/cards/careflow_app_screen.png";

export default function CareFlowCard() {
  const features = [
    {
      icon: Activity,
      title: "Adaptive Workflow Engine",
      desc: "AI that dynamically routes tasks based on patient acuity, staff availability, and real-time demand"
    },
    {
      icon: LayoutDashboard,
      title: "360° Visibility Dashboard",
      desc: "Live status tracking across every care stage, from arrival to discharge"
    },
    {
      icon: Smartphone,
      title: "Omnichannel Patient Touchpoints",
      desc: "Automated SMS, email, voice, and app notifications keep patients informed at every step"
    },
    {
      icon: Zap,
      title: "Intelligent Queue Prioritization",
      desc: "No more bottlenecks; the right patient reaches the right provider, faster"
    },
    {
      icon: BrainCircuit,
      title: "Self-Optimizing Operations",
      desc: "Machine learning continuously refines workflows based on outcomes and patterns"
    }
  ];

  const kpis = [
    { label: "Patient Throughput", value: "+35%", icon: Users, color: "text-emerald-400" },
    { label: "Workflow Efficiency", value: "+45%", icon: TrendingUp, color: "text-blue-400" },
    { label: "Staff Utilization", value: "+30%", icon: UserCheck, color: "text-purple-400" },
    { label: "Patient Wait Time", value: "-50%", icon: Clock, color: "text-amber-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            Live System
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            CareFlow<span style={{ color: ACCENT_COLOR }}>™</span>
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            Streamlined Workflow
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "Where AI meets clinical intuition — seamless workflows that adapt, learn, and accelerate care delivery."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Lens & Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer: Abstract Flow (Clear & Visible) */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-90"
            />
            {/* Subtle overlay to ensure the app screen pops, but background remains visible */}
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>

          {/* Middle Layer: Floating Glass Interface (The App Screen) */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, rotateX: 10 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
            className="relative z-10 w-[95%] max-w-[840px] perspective-1000 group"
          >
            {/* The Screen Itself */}
            <div className="relative w-full bg-white rounded-xl shadow-2xl overflow-hidden border-[6px] border-slate-800 ring-1 ring-white/20 transform transition-transform duration-700 hover:scale-[1.02]">
               <img 
                src={appScreen} 
                alt="App Interface" 
                className="w-full h-auto object-cover bg-slate-100"
              />
              
              {/* Screen Reflection Overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/20 pointer-events-none" />
            </div>

            {/* The Lens Effect - Moving across the screen */}
            <motion.div
              animate={{ 
                top: ["20%", "55%", "35%", "20%"],
                left: ["25%", "65%", "45%", "25%"]
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="absolute w-28 h-28 rounded-full border bg-white/10 backdrop-blur-[2px] overflow-hidden z-20 pointer-events-none"
              style={{
                top: "20%",
                left: "25%",
                transform: "translate(-50%, -50%)",
                borderColor: `${PRIMARY_BLUE}30`,
                boxShadow: `0 0 25px ${PRIMARY_BLUE}30`
              }}
            >
              {/* Inside the lens: Zoomed image positioned inversely */}
              <div className="relative w-[300%] h-[300%] -ml-[100%] -mt-[100%]"> 
                 <motion.img 
                  src={appScreen} 
                  alt="Zoomed Detail" 
                  className="absolute w-full h-full object-cover max-w-none"
                  animate={{ 
                    top: ["-20%", "-55%", "-35%", "-20%"],
                    left: ["-25%", "-65%", "-45%", "-25%"]
                  }}
                  transition={{ 
                    duration: 8, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                />
              </div>
              
              {/* Lens Glare */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
              <div className="absolute inset-0 rounded-full shadow-[inset_0_0_20px_rgba(255,255,255,0.2)]" />
              
              {/* Crosshair/Focus UI */}
              <div className="absolute inset-0 flex items-center justify-center opacity-60">
                <ScanEye className="w-8 h-8 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" style={{ color: PRIMARY_BLUE }} />
              </div>
            </motion.div>

          </motion.div>

          {/* Floating UI Cards (Context) - KPI */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

